/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getCenterCoord } from '#utils/PRIVATE_meterUtils';
import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { VisThresholdsColors, getVisThresholdColor } from '#utils/UNSAFE_meterUtils';
import { styles } from './themes/MeterCircleStyles.css';
import { classNames } from '#utils/UNSAFE_classNames';
import { CSSProperties } from 'preact/compat';
import { getLabelTranslateOffset, getReferenceLineXYOffset } from './layoutUtils';

type getCenterCoordParamType = Parameters<typeof getCenterCoord>;

export type CircularReferenceLineProps = {
  color?: ColorProps['color'] | VisThresholdsColors;
  angle: number;
  radius: string;
  length: string;
  section: getCenterCoordParamType[0];
  label?: string;
  labelMaxWidth: string;
  labelOffset: number;
  labelStyle?: CSSProperties;
  halfMeterOffset: string | number;
};

export function CircularReferenceLine({
  color,
  angle,
  radius,
  length,
  section = 'full',
  label,
  labelMaxWidth,
  labelOffset,
  labelStyle,
  halfMeterOffset
}: CircularReferenceLineProps) {
  const centerCoord = getCenterCoord(section);
  const { xOffset: refXOffset, yOffset: refYOffset } = getReferenceLineXYOffset(
    centerCoord,
    angle,
    radius
  );
  const { xOffset: labelXOffset, yOffset: labelYOffset } = getReferenceLineXYOffset(
    centerCoord,
    angle,
    `calc(${length} + ${radius} + ${labelOffset}px)`
  );
  return (
    <>
      <div
        class={classNames([
          styles.referenceBaseLineStyle,
          color ? undefined : styles.referenceBorderStyle
        ])}
        style={{
          left: refXOffset,
          top: refYOffset,
          width: `calc(${length})`,
          transform: `translate(0, -1px) rotate(${360 - angle}deg)`,
          backgroundColor: color ? getVisThresholdColor(color) : undefined
        }}></div>

      {label && (
        <div
          className={styles.referenceLineLabelContainerStyle}
          style={{
            left: labelXOffset,
            top: labelYOffset,
            maxWidth: labelMaxWidth,
            translate: getLabelTranslateOffset(angle, section, halfMeterOffset),
            ...labelStyle
          }}>
          {label}
        </div>
      )}
    </>
  );
}
